import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { RecordPageShell } from '~/components/layout';
import TemplateForm from '~/components/pages/notifications/templates/TemplateForm';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import {
  emptyTemplateForm,
  type TemplateFormValues,
  toTemplateMessage,
} from '~/lib/notification/templateForm';
import { useCreateTemplateMutation } from '~/modules/query/notification/templates';
import { routes } from '~/app/routes';

const isConflict = (error: unknown): boolean =>
  (error as { status?: number } | null | undefined)?.status === 409;

export default function TemplateCreatePage() {
  const navigate = useNavigate();
  const [createTemplate] = useCreateTemplateMutation();
  const { draft, patch, dirty } = useRecordDraft<null, TemplateFormValues>(null, {
    id: 'new',
    toDraft: emptyTemplateForm,
  });
  const guard = useUnsavedGuard(dirty);
  // A duplicate key belongs on the key field, not in the form-level alert.
  const [duplicate, setDuplicate] = useState(false);

  const save = useRecordSave<TemplateFormValues>({
    save: async (form) => {
      try {
        const created = await createTemplate({
          templateKey: form.templateKey.trim(),
          name: form.name.trim(),
          description: form.description.trim() || null,
          customerIds: form.customerIds,
          message: toTemplateMessage(form),
        }).unwrap();
        guard.release();
        navigate(routes.notificationTemplate(created.id), { replace: true });
      } catch (error) {
        setDuplicate(isConflict(error));
        throw error;
      }
    },
    success: 'Template created as a draft',
    failure: 'The template could not be created. Try again.',
  });

  return (
    <RecordPageShell
      title="Create template"
      breadcrumbs={[
        { label: 'Notifications' },
        { label: 'Templates', href: routes.notificationTemplates },
        { label: 'Create template' },
      ]}
      guard={guard}
    >
      {draft && (
        <TemplateForm
          draft={draft}
          patch={(changes) => {
            if (changes.templateKey !== undefined) setDuplicate(false);
            patch(changes);
          }}
          isNew
          dirty={dirty}
          busy={save.busy}
          error={duplicate ? null : save.error}
          keyError={duplicate ? (save.error ?? undefined) : undefined}
          description="Saved as a draft. Publish it from the template page to make it live."
          onSubmit={() => save.submit(draft)}
        />
      )}
    </RecordPageShell>
  );
}
